import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useRef } from 'react';
import { GroupBase } from 'react-select';
import Select, { components, ControlProps, Props as SelectProps } from 'react-select';

import { useSearchForm } from '@components/SearchForm';
import Translate from '@components/Translate';
import { roomsFilters } from '@constants';
import usePosition from '@hooks/usePosition';
import { usePostMessageListener } from '@hooks/usePostMessageListener';
import { SearchSuggestDestinationResult } from '@hooks/useSearchSuggestDestinations';
import useTranslate from '@hooks/useTranslate';
import { normalize } from '@spazious/jsonapi-tools';
import { useGetCities, useGetHotels } from '@spazious/react-booking-engine';
// UIKit Components
import { FlexBox, Typography } from '@spazious/react-components';
// Icons
import { IconHotel, IconLocation } from '@spazious/react-icons';
import {
  ExpandedPayload,
  IframeEventName,
  PostMessageType,
  ResetCompletedPayload,
  ResetPayload,
} from '@spazious/search-sdk-types';
import { isIOS } from '@utils/isIOS';
import postInteractionEvent from '@utils/sdkEvents';

import { useHotelChainSlug } from '../App/AppHotelChainSlugProvider.js';
type HotelDestinationAutocompleteOption = {
  type: 'hotel' | 'location';
  title: string;
  value: string;
  subtitle: string | null;
};

type CustomSelectProps = SelectProps<
  HotelDestinationAutocompleteOption | null,
  false,
  GroupBase<HotelDestinationAutocompleteOption | null>
>;

export const parseSuggestDestinationToOption = (
  destination: SearchSuggestDestinationResult
): HotelDestinationAutocompleteOption | null => {
  if (destination) {
    if (destination?.type === 'Hotel') {
      return {
        type: 'hotel',
        value: `${destination?.id}`,
        title: destination!.name,
        subtitle: `${destination.city?.country?.name}, ${destination.city.name}`,
      };
    }

    return {
      type: 'location',
      value: `${destination?.id}`,
      title: destination?.name,
      subtitle: destination.country?.name,
    };
  }

  return null;
};

export type HotelDestinationAutocompleteProps = Omit<
  CustomSelectProps,
  | 'onChange'
  | 'onSelect'
  | 'value'
  | 'inputValue'
  | 'onInputChange'
  | 'isClearable'
  | 'cacheOptions'
  | 'isSearchable'
  | 'defaultOptions'
  | 'loadOptions'
> & {
  onSelect?: (evt: null, item: SearchSuggestDestinationResult | null) => void;
  onChange?: (evt: null, option: HotelDestinationAutocompleteOption | null) => void;
  value?: HotelDestinationAutocompleteOption | null;
  fullWidth?: boolean;
};

const CustomControl = ({
  isIframeExpanded,
  pendingDestinationFocus,
  setPendingDestinationFocus,
  ...props
}: ControlProps<any, false> & {
  isIframeExpanded: boolean;
  pendingDestinationFocus: boolean;
  setPendingDestinationFocus: (value: boolean) => void;
}) => {
  const { children, innerProps } = props;
  const controlRef = useRef<HTMLDivElement>(null);

  const handlePointer = useCallback(
    (e: Event) => {
      if (!isIframeExpanded || pendingDestinationFocus) {
        e.preventDefault();
        e.stopPropagation();

        if (!isIframeExpanded && !pendingDestinationFocus) {
          postInteractionEvent(IframeEventName.DESTINATION_FOCUS);
          setPendingDestinationFocus(true);
        }
      }
    },
    [setPendingDestinationFocus, isIframeExpanded, pendingDestinationFocus]
  );

  useEffect(() => {
    const node = controlRef.current;
    if (!node) return;

    node.addEventListener('mousedown', handlePointer, { capture: true });
    node.addEventListener('touchstart', handlePointer, { capture: true });

    return () => {
      node.removeEventListener('mousedown', handlePointer, { capture: true });
      node.removeEventListener('touchstart', handlePointer, { capture: true });
    };
  }, [handlePointer]);

  return (
    <components.Control {...props} innerProps={{ ...innerProps }} innerRef={controlRef}>
      {children}
    </components.Control>
  );
};

export const HotelDestinationAutocomplete: React.FC<HotelDestinationAutocompleteProps> = ({
  onChange,
  onSelect,
  value,
  isLoading,
  ...restProps
}) => {
  const [inputTextValue, setInpuTextValue] = useState<string>();
  const translate = useTranslate();
  const [menuOpen, setMenuOpen] = useState(false);
  const hotelChainSlug = useHotelChainSlug();
  const [isIframeExpanded, setIframeExpanded] = useState(false);
  const [pendingDestinationFocus, setPendingDestinationFocus] = useState(false);

  const [{ bookingType }] = useSearchForm();

  const { data: hotelsQueryResult, loading: hotelsQueryLoading } = useGetHotels({
    variables: {
      include: ['brand', 'country', 'city'],
      filters: {
        nameIcontains: inputTextValue,
        hotelChainSlug: hotelChainSlug,
        ...(bookingType ? roomsFilters[bookingType] : {}),
      },
      pageSize: 100,
      fields: {
        'fields[Hotel]': 'name,city,country,brand,slug',
        'fields[Brand]': 'name,slug',
        'fields[City]': 'name,country,slug',
        'fields[Country]': 'name,slug',
      },
    },
    skip: !hotelChainSlug,
  });

  const hotelsData = useMemo(
    () => (hotelsQueryResult ? normalize(hotelsQueryResult) : []),
    [hotelsQueryResult]
  );

  const { data: commonCitiesQueryResult, loading: commonCitiesQueryLoading } = useGetCities({
    variables: {
      include: ['country'],
      filters: {
        nameIcontains: inputTextValue,
        hotelChainSlug: hotelChainSlug,
        ...(bookingType ? roomsFilters[bookingType] : {}),
      },
      pageSize: 100,
      fields: {
        'fields[City]': 'name,country,slug',
        'fields[Country]': 'name,slug',
      },
    },
    skip: !hotelChainSlug,
  });

  const citiesData = useMemo(
    () => (commonCitiesQueryResult ? normalize(commonCitiesQueryResult) : []),
    [commonCitiesQueryResult]
  );

  const combinedResults = useMemo(() => {
    return [...citiesData, ...hotelsData];
  }, [hotelsData, citiesData]);

  const isEmptyResults = combinedResults.length === 0;
  const selectRef = useRef<any>(null);

  const handleInputChange = useCallback<PickProp<CustomSelectProps, 'onInputChange'>>(
    (inputValue, action) => {
      if (action.action !== 'input-blur' && action.action !== 'menu-close') {
        setInpuTextValue(!!inputValue ? inputValue : undefined);
      }
    },
    []
  );

  const handleChange = useCallback<PickProp<CustomSelectProps, 'onChange'>>(
    (chosen) => {
      const destination = chosen
        ? combinedResults.find((dest) => dest.id === chosen.value) ?? null
        : null;

      onSelect?.(null, destination);
      onChange?.(null, chosen);
      setMenuOpen(false);
      selectRef.current?.blur();
    },
    [combinedResults, onChange, onSelect]
  );

  const formatOptionLabel = useCallback<PickProp<CustomSelectProps, 'formatOptionLabel'>>(
    (option) => {
      return option ? (
        <FlexBox alignItems="center" direction="row" gap={2}>
          <FlexBox>{option.type === 'hotel' ? <IconHotel /> : <IconLocation />}</FlexBox>
          <FlexBox alignItems="flex-start" direction="column" gap={1}>
            <Typography>{option.title}</Typography>
            {option.subtitle && <Typography variant="caption">{option.subtitle}</Typography>}
          </FlexBox>
        </FlexBox>
      ) : null;
    },
    []
  );

  const formattedOptions = useMemo<HotelDestinationAutocompleteOption[]>(
    () => combinedResults.map(parseSuggestDestinationToOption).filter(Boolean) as any,
    [combinedResults]
  );

  const selectedValue = useMemo<HotelDestinationAutocompleteOption | null>(() => {
    return value
      ? {
          title: value.title,
          value: value.value,
          type: value.type,
          subtitle: null,
        }
      : null;
  }, [value]);

  usePostMessageListener<ExpandedPayload>(
    PostMessageType.EXPANDED,
    useCallback(
      (payload: ExpandedPayload) => {
        setIframeExpanded(true);
        if (payload?.context === IframeEventName.DESTINATION_FOCUS && pendingDestinationFocus) {
          setPendingDestinationFocus(false);
          setMenuOpen(true);
          if (!isIOS()) {
            requestAnimationFrame(() => {
              selectRef.current?.focus();
            });
          }
        }
      },
      [pendingDestinationFocus]
    )
  );

  const handleReset = useCallback(() => {
    setMenuOpen(false);
    setIframeExpanded(false);
    setPendingDestinationFocus(false);
  }, []);
  usePostMessageListener<ResetPayload>(PostMessageType.RESET, handleReset);
  usePostMessageListener<ResetCompletedPayload>(PostMessageType.RESET_COMPLETED, handleReset);

  const position = usePosition();

  const CustomControlWrapper = useMemo(
    () => (props: ControlProps<any, false>) =>
      (
        <CustomControl
          {...props}
          isIframeExpanded={isIframeExpanded}
          pendingDestinationFocus={pendingDestinationFocus}
          setPendingDestinationFocus={setPendingDestinationFocus}
        />
      ),
    [isIframeExpanded, pendingDestinationFocus]
  );

  return (
    <Select
      ref={selectRef}
      components={{ Control: CustomControlWrapper }}
      formatOptionLabel={formatOptionLabel}
      isLoading={hotelsQueryLoading || commonCitiesQueryLoading || isLoading}
      loadingMessage={() => <Translate>Loading...</Translate>}
      menuIsOpen={menuOpen}
      noOptionsMessage={() => <Translate>No results</Translate>}
      placeholder={translate('Type Destination or Hotel Name')}
      styles={{
        singleValue: (base) => ({
          ...base,
          color: 'var(--primary-text)',
        }),
        container: (base) => ({
          ...base,
          width: '100%',
          maxWidth: '100%',
        }),
        placeholder: (base) => ({
          ...base,
          paddingLeft: '2.2rem',
          color: 'var(--secondary-text)',
        }),
        control: (base) => ({
          ...base,
          border: 'none',
          boxShadow: 'none',
          minHeight: 'unset',
          width: '100%',
          borderRadius: '0.5rem',
          backgroundColor: 'transparent',
          cursor: 'text',
          transition: '250ms',
          display: 'flex',
        }),
        input: (base) => ({
          ...base,
          padding: '0.5rem',
          fontSize: '1rem',
          fontWeight: 500,
          minWidth: 0,
          color: 'var(--primary-text)',
          '&:focus': {
            outline: 'none',
          },
        }),

        dropdownIndicator: (base, state) => ({
          ...base,
          display: !isEmptyResults ? 'block' : 'none',
          transform: state.selectProps.menuIsOpen
            ? 'scale(1.2) rotate(-180deg)'
            : 'scale(1.2) translate(0px, -2px) rotate(0deg)',
          transition: 'transform 0.2s cubic-bezier(0.2,0,0,1)',
          cursor: 'pointer',
          color: 'var(--primary-text)',
          flexShrink: 0,
        }),
        clearIndicator: (base, state) => ({
          ...base,
          opacity: state.isFocused ? 1 : 0,
          transition: 'opacity 0.2s cubic-bezier(0.2,0,0,1)',
          cursor: 'pointer',
        }),
        indicatorSeparator: () => ({
          display: 'none',
        }),
        valueContainer: (base) => ({
          ...base,
          padding: '0.5rem',
          minWidth: 0,
        }),
        option: (base, { isSelected, isFocused }) => ({
          ...base,
          padding: '0.5rem',
          cursor: 'pointer',
          transition: 'background-color 0.2s cubic-bezier(0.2,0,0,1)',
          backgroundColor: isSelected
            ? 'var(--primary-color)'
            : isFocused
            ? 'var(--background-muted)'
            : 'var(--background-color)',
          color: isSelected ? 'var(--background-color)' : 'var(--primary-text)',
          '&:hover': {
            backgroundColor: isSelected ? 'var(--primary-color)' : 'var(--background-muted)',
          },
        }),

        menu: (base) => ({
          ...base,
          borderRadius: '0.5rem',
          marginTop: position === 'top' ? '0.5rem' : '0',
          marginBottom: position === 'bottom' ? '0.5rem' : '0',
          backgroundColor: 'var(--background-color)',
          boxShadow:
            position === 'top'
              ? '0px 4px 8px rgba(0, 0, 0, 0.1)'
              : '0px -4px 8px rgba(0, 0, 0, 0.1)',
          border: 'none',
          visibility: isEmptyResults && !inputTextValue.length ? 'hidden' : 'visible',
        }),
      }}
      onBlur={() => {
        setMenuOpen(false);
      }}
      onFocus={() => {
        if (isIframeExpanded) {
          setMenuOpen(true);
        }
      }}
      {...restProps}
      isSearchable
      filterOption={() => true}
      inputValue={inputTextValue}
      menuPlacement={position === 'top' ? 'bottom' : 'top'}
      options={formattedOptions}
      value={selectedValue}
      onChange={handleChange}
      onInputChange={handleInputChange}
    />
  );
};

export default HotelDestinationAutocomplete;
